/* ===== HERO STORM (drifting clouds + lightning that strikes NEXON) =====
   Cloud canvas sits behind the title; lightning bolts originate from a cloud
   above the NEXON text and terminate at its top edge. At strike-moment we
   add .is-struck to NEXON so it flashes neon green via CSS. */
(function initHeroStorm() {
  const canvas = document.getElementById('heroCanvas');
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const titleEl = document.querySelector('.hero-title-mark');

  let width = 0;
  let height = 0;
  const clouds = [];
  const CLOUD_COUNT = 100;

  // Offscreen canvas for cloud rendering. We draw all puffs here unblurred,
  // then blit onto the main canvas through a heavy blur filter — that turns
  // the field of distinct circles into one continuous cloud mass instead of
  // a bag of bubbles.
  const cloudCanvas = document.createElement('canvas');
  const cloudCtx = cloudCanvas.getContext('2d');
  let bolt = null;
  let boltAlpha = 0;       // 0..1 visibility of current bolt
  let sceneFlash = 0;      // 0..1 brief illumination boost on cloud during strike

  function resize() {
    width = canvas.width = window.innerWidth;
    height = canvas.height = window.innerHeight;
    cloudCanvas.width = width;
    cloudCanvas.height = height;
  }

  // Cloud band lives in the top of the hero. "Depth" maps Y position to a
  // perspective fake: 0 = overhead (top edge, big and fast), 1 = horizon-ish
  // (bottom of band, small/slow/faded). Together that sells the "looking up
  // at the sky" angle without an actual 3D transform on the canvas.
  class Cloud {
    constructor(initial) {
      this.puffs = [];
      this.randomize(initial);
    }

    randomize(initial) {
      const yMax = Math.max(120, height * 0.32);
      this.y = 4 + Math.random() * yMax;
      this.depth = Math.min(1, this.y / yMax);
      this.scale = (1.0 - this.depth * 0.65) * (0.55 + Math.random() * 0.5);
      this.vx = (0.50 - this.depth * 0.38) * (0.85 + Math.random() * 0.4);
      this.alpha = (0.40 + Math.random() * 0.22) * (1 - this.depth * 0.32);
      this.x = initial ? Math.random() * width : -240 * this.scale;
      this.puffs.length = 0;
      const n = 5 + Math.floor(Math.random() * 4);
      for (let i = 0; i < n; i++) {
        this.puffs.push({
          ox: (Math.random() - 0.5) * 220 * this.scale,
          oy: (Math.random() - 0.5) * 32 * this.scale,
          r: (28 + Math.random() * 48) * this.scale
        });
      }
    }

    update(dt) {
      this.x += this.vx * (dt / 16.67);
      const margin = 240 * this.scale;
      if (this.x > width + margin) this.randomize(false);
    }

    draw(target, illum) {
      const a = this.alpha + illum * 0.28;
      for (const p of this.puffs) {
        const cx = this.x + p.ox;
        const cy = this.y + p.oy;
        const grad = target.createRadialGradient(cx, cy, 0, cx, cy, p.r);
        // Thick body — keep the puff core opaque longer before falling off.
        grad.addColorStop(0, `rgba(68, 76, 92, ${a})`);
        grad.addColorStop(0.6, `rgba(56, 64, 80, ${a * 0.92})`);
        grad.addColorStop(0.9, `rgba(40, 46, 60, ${a * 0.30})`);
        grad.addColorStop(1, 'rgba(20, 22, 28, 0)');
        target.fillStyle = grad;
        target.beginPath();
        target.arc(cx, cy, p.r, 0, Math.PI * 2);
        target.fill();
      }
    }

    // Lowest point of any puff — used as the bolt origin.
    bottomEdge() {
      let bestY = -Infinity;
      let bestX = this.x;
      for (const p of this.puffs) {
        const py = this.y + p.oy + p.r * 0.25;
        if (py > bestY) { bestY = py; bestX = this.x + p.ox; }
      }
      return { x: bestX, y: bestY };
    }
  }

  function init() {
    resize();
    clouds.length = 0;
    for (let i = 0; i < CLOUD_COUNT; i++) clouds.push(new Cloud(true));
  }

  // Build a jagged bolt from (x1,y1) to (x2,y2) with optional branches.
  function generateBolt(x1, y1, x2, y2) {
    const main = [{ x: x1, y: y1 }];
    const dy = y2 - y1;
    const steps = 10 + Math.floor(Math.abs(dy) / 45);
    for (let i = 1; i < steps; i++) {
      const t = i / steps;
      const baseX = x1 + (x2 - x1) * t;
      const baseY = y1 + dy * t;
      // Jitter tapers off near the strike point so the bolt lands accurately.
      const jitter = 34 * (1 - Math.pow(t, 1.6) * 0.75);
      main.push({
        x: baseX + (Math.random() - 0.5) * jitter * 2,
        y: baseY + (Math.random() - 0.5) * 12
      });
    }
    main.push({ x: x2, y: y2 });

    const branches = [];
    const branchCount = Math.random() > 0.35 ? (Math.random() > 0.65 ? 2 : 1) : 0;
    for (let b = 0; b < branchCount; b++) {
      const startIdx = Math.floor(main.length * (0.25 + Math.random() * 0.5));
      const sp = main[startIdx];
      const blen = 4 + Math.floor(Math.random() * 5);
      const segs = [{ x: sp.x, y: sp.y }];
      const dir = Math.random() > 0.5 ? 1 : -1;
      let bx = sp.x, by = sp.y;
      for (let i = 1; i <= blen; i++) {
        bx += dir * (12 + Math.random() * 28);
        by += 18 + Math.random() * 28;
        segs.push({ x: bx, y: by });
      }
      branches.push(segs);
    }
    return { main, branches };
  }

  function strokeSegments(segs) {
    ctx.beginPath();
    for (let i = 0; i < segs.length; i++) {
      if (i === 0) ctx.moveTo(segs[i].x, segs[i].y);
      else ctx.lineTo(segs[i].x, segs[i].y);
    }
    ctx.stroke();
  }

  function drawBolt(b, alpha) {
    if (alpha <= 0 || !b) return;
    ctx.save();
    // Outer green glow halo.
    ctx.strokeStyle = `rgba(74, 222, 128, ${0.55 * alpha})`;
    ctx.shadowColor = '#4ADE80';
    ctx.shadowBlur = 32 * alpha;
    ctx.lineWidth = 4;
    strokeSegments(b.main);
    b.branches.forEach(strokeSegments);
    // Inner near-white core.
    ctx.strokeStyle = `rgba(232, 255, 232, ${alpha})`;
    ctx.shadowBlur = 14 * alpha;
    ctx.lineWidth = 1.4;
    strokeSegments(b.main);
    b.branches.forEach(strokeSegments);
    ctx.restore();
  }

  function fireStrike() {
    if (!titleEl) {
      setTimeout(fireStrike, 4000);
      return;
    }
    const rect = titleEl.getBoundingClientRect();
    // Strike anywhere across the top edge of NEXON.
    const targetX = rect.left + rect.width * (0.18 + Math.random() * 0.64);
    const targetY = rect.top + 6;

    // Prefer a cloud that's actually above the title.
    const above = clouds.filter(c => c.y < targetY - 80);
    const cloud = (above.length > 0 ? above : clouds)[
      Math.floor(Math.random() * (above.length || clouds.length))
    ];
    const start = cloud.bottomEdge();

    bolt = generateBolt(start.x, start.y, targetX, targetY);
    boltAlpha = 1;
    sceneFlash = 1;
    titleEl.classList.add('is-struck');

    // Re-jitter several times during the longer strike for a flicker effect.
    setTimeout(() => { if (boltAlpha > 0.55) bolt = generateBolt(start.x, start.y, targetX, targetY); }, 60);
    setTimeout(() => { if (boltAlpha > 0.35) bolt = generateBolt(start.x, start.y, targetX, targetY); }, 160);
    setTimeout(() => { if (boltAlpha > 0.18) bolt = generateBolt(start.x, start.y, targetX, targetY); }, 280);
    setTimeout(() => titleEl.classList.remove('is-struck'), 240);

    // Next strike in ~3s.
    setTimeout(fireStrike, 3000);
  }

  let last = performance.now();
  function loop(now) {
    const dt = now - last;
    last = now;

    // Render unblurred puffs to offscreen, then composite onto main canvas
    // through a heavy blur. The blur smears the individual circles together
    // into continuous cloud cover.
    cloudCtx.clearRect(0, 0, width, height);
    clouds.forEach(c => { c.update(dt); c.draw(cloudCtx, sceneFlash); });

    ctx.clearRect(0, 0, width, height);
    ctx.save();
    ctx.filter = 'blur(14px)';
    ctx.drawImage(cloudCanvas, 0, 0);
    ctx.restore();

    if (boltAlpha > 0) {
      drawBolt(bolt, boltAlpha);
      boltAlpha = Math.max(0, boltAlpha - dt / 620);
    }
    if (sceneFlash > 0) sceneFlash = Math.max(0, sceneFlash - dt / 480);

    requestAnimationFrame(loop);
  }

  window.addEventListener('resize', () => {
    resize();
    clouds.forEach(c => c.randomize(true));
  });

  init();
  requestAnimationFrame(loop);
  setTimeout(fireStrike, 1800); // first strike a beat after the page settles
})();

/* ===== NAV SCROLL BEHAVIOR ===== */
(function initNav() {
  const navbar = document.getElementById('navbar');
  const hamburger = document.getElementById('hamburger');
  const mobileMenu = document.getElementById('mobileMenu');
  if (!navbar || !hamburger || !mobileMenu) return;

  window.addEventListener('scroll', () => {
    navbar.classList.toggle('scrolled', window.scrollY > 40);
  }, { passive: true });

  hamburger.addEventListener('click', () => {
    const open = mobileMenu.classList.toggle('open');
    hamburger.classList.toggle('active', open);
  });

  mobileMenu.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', () => {
      mobileMenu.classList.remove('open');
      hamburger.classList.remove('active');
    });
  });
})();

/* ===== HERO ENTRANCE ANIMATION ===== */
(function initHeroReveal() {
  const elements = document.querySelectorAll('.hero-eyebrow, .hero-title-ghost, .hero-title-mark, .hero-sub, .hero-actions');
  requestAnimationFrame(() => {
    elements.forEach(el => el.classList.add('loaded'));
  });
})();

/* ===== SCROLL REVEAL ===== */
(function initScrollReveal() {
  const revealEls = document.querySelectorAll(
    '.reveal, .reveal-left, .reveal-right, ' +
    '.reveal-delay-1, .reveal-delay-2, .reveal-delay-3, ' +
    '.reveal-delay-4, .reveal-delay-5, .reveal-delay-6'
  );

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in-view');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

  revealEls.forEach(el => {
    if (!el.closest('#hero')) observer.observe(el);
  });
})();

/* ===== ANIMATED COUNTERS =====
   Stat targets that aren't quantitative (e.g. "2022" as a year) still
   animate; the count-up reads the data-target literally and eases in. */
(function initCounters() {
  const statNums = document.querySelectorAll('.stat-num[data-target]');
  let triggered = false;

  function animateCounter(el) {
    const target = parseInt(el.getAttribute('data-target'), 10);
    const duration = 2000;
    const start = performance.now();

    function update(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = Math.round(eased * target).toLocaleString();
      if (progress < 1) requestAnimationFrame(update);
    }

    requestAnimationFrame(update);
  }

  const heroObserver = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting && !triggered) {
      triggered = true;
      statNums.forEach(el => animateCounter(el));
    }
  }, { threshold: 0.3 });

  const heroEl = document.getElementById('hero');
  if (heroEl) heroObserver.observe(heroEl);
})();

/* ===== FORM HANDLING ===== */
(function initForm() {
  const form = document.getElementById('inquireForm');
  const success = document.getElementById('formSuccess');
  if (!form) return;

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const emailInput = document.getElementById('email');
    if (!emailInput.value || !emailInput.value.includes('@')) {
      emailInput.style.borderColor = 'rgba(64,145,108,0.85)';
      emailInput.focus();
      return;
    }
    const btnText = form.querySelector('.btn-text');
    const btnLoader = form.querySelector('.btn-loader');
    btnText.style.display = 'none';
    btnLoader.style.display = 'inline';

    setTimeout(() => {
      form.style.display = 'none';
      success.style.display = 'block';
    }, 1200);
  });
})();

/* ===== SMOOTH ACTIVE NAV HIGHLIGHT ===== */
(function initActiveNav() {
  const sections = document.querySelectorAll('section[id]');
  const navLinks = document.querySelectorAll('.nav-links a:not(.nav-cta)');

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const id = entry.target.getAttribute('id');
        navLinks.forEach(link => {
          link.style.color = link.getAttribute('href') === `#${id}` ? '#ffffff' : '';
        });
      }
    });
  }, { threshold: 0.4 });

  sections.forEach(s => observer.observe(s));
})();

/* ===== GALLERY TILT ===== */
(function initGalleryTilt() {
  document.querySelectorAll('.gallery-item').forEach(card => {
    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      card.style.transform = `perspective(800px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg) scale(1.02)`;
    });
    card.addEventListener('mouseleave', () => {
      card.style.transform = '';
    });
  });
})();
